import * as React from "react";
import { merge, $, media } from "glamor";
import theme from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import { Map, Set } from "immutable";
import classNames from "classnames";
import { Calendar, IDatePickerStrings, DayOfWeek } from "@nuorder/components";
import {
    IDate,
    isValidDateFormat,
    generateDateValueObject,
} from "../product-collection-grid-view/grid-editors/editor-helper";
import { getDatePickerString } from "helpers/dates";
import { DATATEST } from "constants/integration";
import { startOfDay } from "date-fns";
import { mapValuesToDateObj } from "helpers/ag-grid";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IFieldProps {
    value: any; // Ag-grid rowNode reference to this row
    field: Map<string, any>;
    readOnly: boolean;
    onChange: any;
    bindEditor: any;
    hashtags: Set<string>;
    onKeyDown: (e /*KeyboardEvent<HTMLDivElement>*/) => void;
}

interface IState {
    value: IDate;
    editMode: EDIT_MODES;
    inputString: string;
    initDate: Date;
    fromCalendar: boolean;
    focus: boolean;
    remove: boolean;
    errorMessage?: string;
    calenderFocus: boolean;
}

enum EDIT_MODES {
    REPLACE,
    APPEND,
}

export class DateField extends React.PureComponent<IFieldProps, IState> {
    styles = merge(
        $(" .inputTextBox", {
            padding: "10px",
            minWidth: "160px",
            width: "100%",
            zIndex: "100",
        }),
        $(" .inputTextBox:hover", {
            background: theme.palette.editing,
        }),
        $(".isFocused .inputTextBox", {
            background: theme.palette.editing,
        }),
        $(" .placeholder", {
            position: "absolute",
            top: "10px",
            left: "10px",
            color: theme.palette.placeholderText,
            fontSize: "14px",
        }),
        media(
            "(max-width: 480px)",
            $(" .placeholder", {
                fontSize: "12px",
            })
        )
    );

    private inputRef;
    private defaultValue = undefined;

    constructor(props) {
        super(props);
        let editMode = EDIT_MODES.APPEND;
        let value = this.props.value;
        let remove = false;
        const fromCalendar = false;
        this.defaultValue = generateDateValueObject(value, undefined, ["M/D/YYYY"], true);
        let inputString = value;
        let initDate;
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            value = undefined;
            remove = true;
        } else if (props.charPress) {
            value = this.defaultValue;
            inputString = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        } else {
            value = this.defaultValue;
        }
        if (
            this.defaultValue?.year !== undefined &&
            this.defaultValue?.month !== undefined &&
            this.defaultValue?.day !== undefined
        ) {
            const dateString = `${this.defaultValue?.month}/${this.defaultValue?.day}/${this.defaultValue?.year}`;
            initDate = new Date(dateString);
        } else {
            initDate = new Date();
        }
        this.state = {
            value,
            editMode,
            inputString,
            initDate,
            fromCalendar,
            remove,
            focus: false,
            calenderFocus: false,
        };
        this.inputRef = React.createRef();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // Do not update if typing
        let initDate;
        this.defaultValue = generateDateValueObject(nextProps.value, undefined, ["M/D/YYYY"], true);
        if (
            this.defaultValue?.year !== undefined &&
            this.defaultValue?.month !== undefined &&
            this.defaultValue?.day !== undefined
        ) {
            const dateString = `${this.defaultValue?.month}/${this.defaultValue?.day}/${this.defaultValue?.year}`;
            initDate = new Date(dateString);
        } else {
            initDate = new Date();
        }
        if (!this.state.focus) {
            this.setState({
                value: nextProps.value,
                inputString: nextProps.value,
                initDate,
            });
        }
    }

    handleFocus = () => {
        this.setState({ focus: true, calenderFocus: true });
    };

    calenderFocusChange = () => {
        this.setState({ calenderFocus: true, focus: true });
    };

    handleBlur = () => {
        setTimeout(() => {
            this.setState({ focus: false, calenderFocus: false, errorMessage: "" });
        }, 300);
    };

    render() {
        const dayPickerStrings: IDatePickerStrings = getDatePickerString();
        const { field, readOnly, onKeyDown } = this.props;
        const { focus, errorMessage, calenderFocus } = this.state;
        return (
            <div
                {...this.styles}
                className={classNames("fieldContainer", { isFocused: focus, editable: !readOnly })}
                data-field={field.get("field_name")}
            >
                <span className="fieldLabel">{field.get("display_name")}</span>
                <div
                    className="inputContainer"
                    data-test={DATATEST.ASSORTMENTS.GRID.DATE_PICKER_CELL_EDITOR}
                    onMouseLeave={this.handleBlur}
                    onMouseEnter={this.handleFocus}
                >
                    <input
                        className="inputTextBox"
                        placeholder={formatMessage("MM/DD/YYYY")}
                        onChange={this.onTextChange.bind(this)}
                        autoComplete={"off"}
                        value={this.state.inputString}
                        onFocus={this.handleFocus}
                    />
                    {errorMessage && <div className="errorMessage">{errorMessage}</div>}
                    {calenderFocus && (
                        <Calendar
                            onSelectDate={this.onSelectDate}
                            isMonthPickerVisible={false}
                            showGoToToday={false}
                            firstDayOfWeek={DayOfWeek.Sunday}
                            strings={dayPickerStrings}
                            today={this.state.initDate}
                            onFocus={this.calenderFocusChange}
                        />
                    )}
                </div>
            </div>
        );
    }

    bindRef(ref) {
        this.inputRef.current = ref;
    }

    onTextChange(e) {
        const newValue = e.target.value;
        const isValidDate = isValidDateFormat(newValue, ["M/D/YYYY"], false);
        const { field, onChange } = this.props;
        let errorMessage = "";
        let initDate;

        const valueMap = generateDateValueObject(newValue, undefined, ["M/D/YYYY"], true);
        if (!isValidDate && newValue !== "") {
            errorMessage = "Invalid Date";
        } else {
            onChange({ value: valueMap, field });
        }

        if (valueMap?.year !== undefined && valueMap?.month !== undefined && valueMap?.day !== undefined) {
            const dateString = `${valueMap?.month}/${valueMap?.day}/${valueMap?.year}`;
            initDate = new Date(dateString);
        } else {
            initDate = new Date();
        }

        this.setState({
            inputString: newValue,
            errorMessage,
            initDate,
        });
    }

    onSelectDate = (date: Date | null | undefined): void => {
        const { field, onChange } = this.props;
        if (date !== null && date !== undefined) {
            const inputDate = startOfDay(date);
            const value = mapValuesToDateObj(date);
            const dateString = `${value.month}/${value.day}/${value.year}`;

            const valueMap = Map({
                year: inputDate.getFullYear(),
                month: inputDate.getMonth() + 1,
                day: inputDate.getDate(),
            });

            this.setState({
                value,
                fromCalendar: true,
                inputString: dateString,
                initDate: new Date(dateString),
                focus: false,
                calenderFocus: false,
            });
            onChange({ value: valueMap, field });
        }
    };
}
